import { Component, OnInit } from '@angular/core';
import { AlertService } from 'src/app/services/alert.service';
import { DataService } from 'src/app/services/data.service';
import { PosTerminalService } from 'src/app/services/pos-terminal.service';
import { CaseService } from 'src/app/services/case.service';
import { SelectItem } from 'src/app/core/models/responses/select-item';
import { PosCommissionRate, PosTerminal } from 'src/app/models/pos/pos-terminal';
import { PosCommissionValueType } from 'src/app/models/enums/pos-commission-value-type';
import { CaseType } from 'src/app/models/enums/case-type';
import { Case } from 'src/app/models/case/case';
import { PosDevice } from 'src/app/models/pos/pos-device';
import { PosDeviceService } from 'src/app/services/pos-device.service';
import { extractErrorMessage } from '../../accounting/shared/e-invoice.helpers';

/**
 * POS (kredi kartı) kasalarının ayarları: bağlı banka, peşin/taksitli kabul durumu ve
 * taksit başına komisyon oranları.
 *
 * Komisyon oranı terminalde tek alan değil taksit başına satırdır, çünkü gerçek POS
 * sözleşmelerinde peşin ile 6 taksit farklı oranlanır. Hızlı satışta POS kasası seçilince
 * taksit listesi buradan gelir; komisyon, tahsilattan ayrı bir gider hareketi olarak aynı
 * kasaya yazılır.
 *
 * Kasa bir POS cihazına bağlanırsa hızlı satışta kart tutarı cihaza gönderilebilir
 * (pos-entegrasyonu.md K6). Cihaz bağı terminal kaydından ayrı uçla yazılır: terminal
 * kaydedildikten sonra, değiştiyse.
 */
interface DeviceLink {
  posDeviceId: number | null;
  code: string | null;
}

@Component({
  selector: 'app-pos-terminal',
  templateUrl: './pos-terminal.component.html',
  styleUrls: ['./pos-terminal.component.scss']
})
export class PosTerminalComponent implements OnInit {
  breadCrumbItems: Array<{}> = [{ label: 'Kasa' }, { label: 'POS Ayarları', active: true }];

  readonly PosCommissionValueType = PosCommissionValueType;
  currentBranch = JSON.parse(localStorage.getItem('currentBranch'));

  terminals: PosTerminal[] = [];
  banks: SelectItem[] = [];
  /** POS ayarı tanımlanabilecek kasalar: bankaya bağlı olmayanlar. */
  availableCases: Case[] = [];
  /** Bu şubeye ya da şubesiz tanımlı etkin cihazlar. */
  devices: PosDevice[] = [];
  /** Düzenlemeye başlarken kayıtlı cihaz bağı — değişmediyse bağ ucu çağrılmaz. */
  private savedDeviceLink: DeviceLink = { posDeviceId: null, code: null };

  model: PosTerminal = this.getEmptyModel();
  isEditing = false;
  isDisabled = false;

  constructor(
    private service: PosTerminalService,
    private caseService: CaseService,
    private dataService: DataService,
    private alertService: AlertService,
    private posDeviceService: PosDeviceService
  ) { }

  ngOnInit(): void {
    this.dataService.getBank().toPromise().then(res => this.banks = res.data || []);
    this.loadTerminals();
    this.loadCases();
    this.loadDevices();
  }

  loadDevices() {
    this.posDeviceService.list(true).subscribe({
      next: res => this.devices = (res.data || [])
        .filter(device => !device.branchId || device.branchId === this.currentBranch.id),
      // Cihaz entegrasyonu kullanılmıyorsa liste boş kalır; POS ayarı yine yapılabilir.
      error: () => this.devices = []
    });
  }

  getEmptyModel(): PosTerminal {
    return {
      allowsSingle: true,
      allowsInstallment: false,
      maxInstallmentCount: 1,
      commissionRates: [{ installmentCount: 1, valueType: PosCommissionValueType.Rate, value: 0 }]
    };
  }

  loadTerminals() {
    this.service.getAllWoutPaging({ branchId: this.currentBranch.id }).toPromise()
      .then(res => this.terminals = res.data?.Items || []);
  }

  loadCases() {
    this.caseService.getAllWoutPaging({ branchId: this.currentBranch.id }).toPromise()
      .then(res => {
        // Bankaya bağlı kasaya elle hareket girilemediği için POS ayarı tanımlanamaz.
        this.availableCases = (res.data?.Items || []).filter(item => !item.hasBankAccount);
      });
  }

  getCaseName(caseId?: number): string {
    return this.availableCases.find(item => item.id === caseId)?.name || '';
  }

  // ── Form ────────────────────────────────────────────────────────────────────

  newTerminal() {
    this.model = this.getEmptyModel();
    this.isEditing = false;
    this.savedDeviceLink = { posDeviceId: null, code: null };
  }

  edit(terminal: PosTerminal) {
    // Kopya: iptal edilen düzenleme listedeki satırı bozmasın.
    this.model = {
      ...terminal,
      commissionRates: (terminal.commissionRates || []).map(rate => ({ ...rate }))
    };
    this.isEditing = true;
    this.savedDeviceLink = { posDeviceId: terminal.posDeviceId ?? null, code: terminal.providerPaymentMethodCode ?? null };
  }

  addRate() {
    const used = (this.model.commissionRates || []).map(rate => rate.installmentCount || 0);
    // Boştaki ilk taksit sayısını öner: 1 (peşin), sonra 2, 3 ...
    let next = 1;
    while (used.includes(next)) next++;
    this.model.commissionRates.push({ installmentCount: next, valueType: PosCommissionValueType.Rate, value: 0 });
  }

  removeRate(index: number) {
    this.model.commissionRates.splice(index, 1);
  }

  isValid(): boolean {
    const messages: string[] = [];
    const rates = this.model.commissionRates || [];

    if (!this.model.caseId) messages.push('Kasa seçiniz.');
    if (!this.model.bankId) messages.push('Bağlı banka seçiniz.');
    if (!this.model.allowsSingle && !this.model.allowsInstallment)
      messages.push('Peşin veya taksitli ödemeden en az biri açık olmalıdır.');
    if (this.model.allowsInstallment && (this.model.maxInstallmentCount || 0) < 2)
      messages.push('Taksitli ödeme açıkken en yüksek taksit sayısı en az 2 olmalıdır.');

    const installments = rates.map(rate => rate.installmentCount);
    if (new Set(installments).size !== installments.length)
      messages.push('Aynı taksit sayısı için birden fazla komisyon oranı tanımlanamaz.');
    if (rates.some(rate => (rate.installmentCount || 0) < 1))
      messages.push('Taksit sayısı en az 1 olmalıdır (1 = peşin).');
    if (rates.some(rate => (rate.value || 0) < 0))
      messages.push('Komisyon değeri negatif olamaz.');
    if (this.model.allowsSingle && !rates.some(rate => rate.installmentCount === 1))
      messages.push('Peşin ödeme açıkken 1 taksit (peşin) komisyon oranı tanımlanmalıdır.');
    if (rates.some(rate => rate.installmentCount !== 1 && rate.installmentCount > (this.model.maxInstallmentCount || 0)))
      messages.push('Komisyon oranlarındaki taksit sayısı, en yüksek taksit sayısını aşamaz.');

    if (messages.length > 0) {
      this.alertService.showErrorsToast(messages);
      return false;
    }
    return true;
  }

  submit() {
    if (!this.isValid()) return;
    this.isDisabled = true;
    const request = this.isEditing ? this.service.update(this.model) : this.service.create(this.model);
    const caseId = this.model.caseId!;
    const deviceLink: DeviceLink = {
      posDeviceId: this.model.posDeviceId ?? null,
      code: this.model.providerPaymentMethodCode?.trim() || null
    };
    request.toPromise().then(async res => {
      if (!res.isSuccess) {
        this.isDisabled = false;
        return;
      }
      const deviceSaved = await this.saveDeviceLink(caseId, deviceLink);
      this.isDisabled = false;
      if (deviceSaved)
        this.alertService.showSuccess(this.isEditing ? 'POS ayarı güncellendi.' : 'POS ayarı eklendi.');
      this.newTerminal();
      this.loadTerminals();
      this.loadCases();
      this.loadDevices();
      // Kasa tipi arka uçta POS olarak işaretlendi; kasa listesi tazelensin.
      this.caseService.refreshIndex.next('');
    }).catch(() => this.isDisabled = false);
  }

  /**
   * Cihaz bağını terminal kaydından sonra yazar; değişmediyse istek atılmaz. Terminal
   * kaydedildi ama bağ yazılamadıysa kullanıcıya ayrıca söylenir.
   */
  private async saveDeviceLink(caseId: number, link: DeviceLink): Promise<boolean> {
    if (link.posDeviceId === this.savedDeviceLink.posDeviceId && link.code === this.savedDeviceLink.code)
      return true;
    try {
      await this.posDeviceService.assignTerminal({
        caseId,
        posDeviceId: link.posDeviceId,
        providerPaymentMethodCode: link.posDeviceId ? link.code : null
      }).toPromise();
      return true;
    } catch (error) {
      this.alertService.showErrorsToast([
        'POS ayarı kaydedildi ancak cihaz bağı yazılamadı: ' + extractErrorMessage(error, 'bilinmeyen hata.')
      ]);
      return false;
    }
  }

  delete(terminal: PosTerminal) {
    this.alertService
      .showConfirmAlert(
        `"${terminal.caseName}" kasasının POS ayarı silinecek ve kasa nakit kasaya dönecek. ` +
        'Geçmiş komisyon hareketleri kasada kalır. Devam etmek istiyor musunuz?',
        'Evet, Sil',
        'Vazgeç'
      )
      .then(result => {
        if (!result.isConfirmed) return;
        this.service.delete(terminal.id).toPromise().then(() => {
          this.alertService.showSuccess('POS ayarı silindi.');
          this.newTerminal();
          this.loadTerminals();
          this.caseService.refreshIndex.next('');
        });
      });
  }
}
